import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { BellIcon, CalendarDaysIcon, MapIcon, MaximizeIcon, MinimizeIcon, TruckIcon } from 'lucide-react';
import { collections, liveCollection, materials } from '../../data/collectes';
import { distanceKm, routeLengthKm } from '../../utils/geo';
const TRUCK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';
const HOME_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 10v10h14V10"/></svg>';
const truckIcon = L.divIcon({
  className: 'collecte-truck',
  html: `<span class="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-ville-blue shadow-lg shadow-ville-navy/30">${TRUCK_SVG}</span>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18]
});
const homeIcon = L.divIcon({
  className: 'collecte-home',
  html: `<span class="grid h-8 w-8 place-items-center rounded-full border-[3px] border-white bg-ville-navy shadow-md shadow-ville-navy/30">${HOME_SVG}</span>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});
const AVERAGE_SPEED_KMH = 12;
const TICK_MS = 700;
const PROGRESS_PER_TICK = 0.004;

/** Teal de Windsor, pour le trajet qu'il reste à parcourir. */
const ROUTE_COLOR = '#0d4f46';

/** Libellé de la prochaine collecte, repris de la liste pour ne jamais la contredire. */
const nextCollection = collections[1];
const nextCollectionLabel = `${nextCollection.weekday} ${nextCollection.date}`;

/** Longueurs cumulées, pour interpoler une position à partir d'un pourcentage de trajet. */
function cumulativeLengths(points: [number, number][]): number[] {
  const totals = [0];
  for (let i = 1; i < points.length; i += 1) {
    totals.push(totals[i - 1] + distanceKm(points[i - 1], points[i]));
  }
  return totals;
}
function pointAt(points: [number, number][], totals: number[], progress: number): [number, number] {
  const target = totals[totals.length - 1] * progress;
  const index = totals.findIndex(value => value >= target);
  if (index <= 0) return points[0];
  const previous = totals[index - 1];
  const ratio = (target - previous) / Math.max(totals[index] - previous, 1e-9);
  const [lat1, lng1] = points[index - 1];
  const [lat2, lng2] = points[index];
  return [lat1 + (lat2 - lat1) * ratio, lng1 + (lng2 - lng1) * ratio];
}
function splitRoute(points: [number, number][], totals: number[], progress: number) {
  const target = totals[totals.length - 1] * progress;
  const cut = Math.max(1, totals.findIndex(value => value >= target));
  const truck = pointAt(points, totals, progress);
  return {
    done: [...points.slice(0, cut), truck] as [number, number][],
    remaining: [truck, ...points.slice(cut)] as [number, number][],
    truck
  };
}
interface CollectionTrackerProps {
  remindersOpen?: boolean;
  onToggleReminders?: () => void;
}
export function CollectionTracker({
  remindersOpen = false,
  onToggleReminders
}: CollectionTrackerProps) {
  const {
    route: fallbackRoute,
    home,
    startProgress,
    truck,
    material
  } = liveCollection;
  const [route, setRoute] = useState<[number, number][]>(fallbackRoute);
  const [progress, setProgress] = useState(startProgress);
  const [expanded, setExpanded] = useState(false);
  const [collectionToday, setCollectionToday] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const totals = useMemo(() => cumulativeLengths(route), [route]);
  const {
    done,
    remaining,
    truck: truckPosition
  } = useMemo(() => splitRoute(route, totals, progress), [route, totals, progress]);
  const distance = routeLengthKm(remaining);
  const minutes = Math.max(1, Math.round(distance / AVERAGE_SPEED_KMH * 60));
  const arrived = progress >= 0.999;
  const arrivingSoon = minutes <= 10;

  /** Trajet réel sur le réseau routier (OSRM, sans clé d'API). */
  useEffect(() => {
    const start = fallbackRoute[0];
    const url = `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${home[1]},${home[0]}?overview=full&geometries=geojson`;
    let cancelled = false;
    fetch(url).then(response => response.ok ? response.json() : Promise.reject(new Error('routing'))).then((data: {
      routes?: {
        geometry: {
          coordinates: [number, number][];
        };
      }[];
    }) => {
      const coordinates = data.routes?.[0]?.geometry?.coordinates;
      if (cancelled || !coordinates || coordinates.length < 2) return;
      setRoute(coordinates.map(([lng, lat]) => [lat, lng] as [number, number]));
    }).catch(() => {
      /* On garde le trajet approximatif si le service de routage est indisponible. */
    });
    return () => {
      cancelled = true;
    };
  }, [fallbackRoute, home]);
  useEffect(() => {
    if (arrived) return;
    const timer = window.setInterval(() => {
      setProgress(current => Math.min(1, current + PROGRESS_PER_TICK));
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [arrived]);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: home,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [home]);
  useEffect(() => {
    if (!mapRef.current) return;
    if (!collectionToday) {
      mapRef.current.setView(home, 15);
      return;
    }
    if (route.length < 2) return;
    mapRef.current.fitBounds(L.latLngBounds(route), {
      padding: [34, 34]
    });
  }, [route, collectionToday, home]);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!collectionToday) {
      L.marker(home, {
        icon: homeIcon,
        title: 'Votre adresse',
        alt: 'Votre adresse'
      }).addTo(layer);
      return;
    }
    L.polyline(done, {
      color: '#94a3b8',
      weight: 4,
      opacity: 0.65
    }).addTo(layer);
    L.polyline(remaining, {
      color: ROUTE_COLOR,
      weight: 5,
      dashArray: '1 9',
      lineCap: 'round'
    }).addTo(layer);
    L.marker(home, {
      icon: homeIcon,
      title: 'Votre adresse',
      alt: 'Votre adresse'
    }).addTo(layer);
    L.marker(truckPosition, {
      icon: truckIcon,
      title: truck,
      alt: `Position du ${truck}`
    }).bindTooltip(truck, {
      direction: 'top',
      offset: [0, -18]
    }).addTo(layer);
  }, [done, remaining, truckPosition, home, truck, collectionToday]);

  /** Redimensionne et recadre la carte quand la fiche passe en plein écran et revient. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;
      map.invalidateSize();
      if (!collectionToday) {
        map.setView(home, 15);
        return;
      }
      if (route.length > 1) map.fitBounds(L.latLngBounds(route), {
        padding: [34, 34]
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [expanded, route, collectionToday, home]);
  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded]);
  const card = <section aria-labelledby="suivi-collecte-titre" className={expanded ? 'relative isolate m-auto flex h-full w-full max-w-[1000px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-ville-navy/30' : 'relative isolate z-0 flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white'} data-id="element-751" data-mp="1g3q4udwg41lp">
      <div className="flex items-start justify-between gap-3 px-5 pb-4 pt-5" data-id="element-752" data-mp="x53xwie8zg9v">
        <div className="min-w-0" data-id="element-753" data-mp="x785pbf9ozff">
          <h2 id="suivi-collecte-titre" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-754" data-mp="1167ex8817ico">
            <TruckIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-755" data-mp="163fowlj10g2u" />
            {collectionToday ? 'Collecte en cours' : 'Suivi du camion en direct'}
          </h2>
          <p className="mt-0.5 truncate text-sm text-slate-500" data-id="element-756" data-mp="2hje5rbkg46em">
            {collectionToday ? materials[material].label : `Prochaine collecte · ${nextCollectionLabel}`}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${!collectionToday ? 'bg-slate-100 text-slate-600' : arrivingSoon ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-600'}`} data-id="element-757" data-mp="2mqcfembalme4">
          {!collectionToday ? 'Hors service' : arrived ? 'Sur place' : arrivingSoon ? 'Arrive bientôt' : 'En route'}
        </span>
      </div>

      <div className={expanded ? 'relative min-h-0 flex-1' : 'relative'} data-id="element-758" data-mp="1uywle77eoxdv">
        <div ref={containerRef} role="application" aria-label={`Trajet du ${truck} vers votre adresse`} className={expanded ? 'h-full w-full bg-ville-mist' : 'h-[260px] w-full bg-ville-mist sm:h-[300px]'} data-id="element-759" data-mp="1v0prnle3xflc" />
        <button type="button" onClick={() => setExpanded(current => !current)} aria-label={expanded ? 'Réduire la carte' : 'Agrandir la carte'} className="absolute left-3 top-3 z-[500] inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3.5 py-2 text-xs font-bold text-ville-navy shadow-md shadow-ville-navy/10 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-760" data-mp="yrm5kw8z8uwn">
          {expanded ? <MinimizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-761" data-mp="ps8szupk3q7p" /> : <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-762" data-mp="l1im2d1hffaw" />}
          {expanded ? 'Réduire' : 'Agrandir'}
        </button>

        {!collectionToday && <div className="absolute inset-0 z-[450] grid place-items-center bg-ville-navy/45 p-5 backdrop-blur-[2px]" data-id="element-763" data-mp="3teplo1n18yx2">
            <div className="max-w-sm rounded-2xl bg-white/95 px-5 py-4 text-center shadow-lg shadow-ville-navy/20" data-id="element-764" data-mp="2406tlddxobwn">
              <p className="font-extrabold text-ville-navy" data-id="element-765" data-mp="28r6mm7jxm9tv">Aucune collecte aujourd’hui</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-766" data-mp="1uzs6c8hesmqi">
                Votre prochaine collecte est le {nextCollectionLabel.toLowerCase()}. Le camion apparaîtra ici, en
                direct sur la carte, le jour de la collecte.
              </p>
            </div>
          </div>}
      </div>

      {collectionToday ? <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100" data-id="element-767" data-mp="2roq1vswzgooq">
          <div className="px-5 py-4 text-center" data-id="element-768" data-mp="2dp862tl9sb5v">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-769" data-mp="2iokx7qj6c4e9">Arrivée estimée</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-blue" data-id="element-770" data-mp="12a1obocnp0p4">
              {arrived ? 'Sur place' : `${minutes} min`}
            </p>
          </div>
          <div className="px-5 py-4 text-center" data-id="element-771" data-mp="177bex7bs7iej">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-772" data-mp="38xij7wrnywtv">Distance actuelle</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-navy" data-id="element-773" data-mp="3411ylcjob0lj">
              {distance < 0.1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1).replace('.', ',')} km`}
            </p>
          </div>
        </div> : <div className="border-t border-slate-100 px-5 py-4 text-center" data-id="element-774" data-mp="2q82jvcrptaf1">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-775" data-mp="2q5zi5vh79suh">Prochaine collecte</p>
          <p className="mt-1 text-2xl font-extrabold text-ville-navy" data-id="element-776" data-mp="2lblqa31qy3uk">{nextCollectionLabel}</p>
        </div>}

      {!expanded && <div className="border-t border-slate-100 px-5 py-3 text-center" data-id="element-777" data-mp="2c9mblhe7fbus">
          <button type="button" onClick={() => setCollectionToday(current => !current)} aria-pressed={!collectionToday} className="text-xs font-bold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-778" data-mp="eijca3w49sd2">
            {collectionToday ? 'Aperçu d’une journée sans collecte' : 'Revenir au suivi en direct'}
          </button>
        </div>}

      {!expanded && <nav aria-label="Raccourcis collectes" className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100" data-id="element-779" data-mp="265g0jxcs6f49">
          {[{
        to: '/mes-collectes/calendrier',
        label: 'Calendrier',
        Icon: CalendarDaysIcon
      }, {
        to: '/plan',
        label: 'Carte',
        Icon: MapIcon
      }].map(({
        to,
        label,
        Icon
      }) => <Link key={label} to={to} className="flex flex-col items-center gap-1.5 px-2 py-4 text-xs font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-sand hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-780" data-mp="1ngsvheultpqo">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-781" data-mp="2xfklqymzwpre">
                <Icon className="h-5 w-5" data-id="element-782" data-mp="36fz5vuo83ytb" />
              </span>
              {label}
            </Link>)}
          <button type="button" onClick={onToggleReminders} aria-expanded={remindersOpen} className={`flex flex-col items-center gap-1.5 px-2 py-4 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${remindersOpen ? 'bg-ville-sand text-ville-deep' : 'text-ville-navy hover:bg-ville-sand hover:text-ville-deep'}`} data-id="element-783" data-mp="32ex9dlloy0v1">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-784" data-mp="2y8r0bz0egpm">
              <BellIcon className="h-5 w-5" data-id="element-785" data-mp="hk4k5dy7s6ee" />
            </span>
            Rappels
          </button>
        </nav>}
    </section>;
  if (!expanded) return card;
  return <div role="dialog" aria-modal="true" aria-label="Carte de collecte agrandie" className="fixed inset-0 z-[60] flex bg-ville-navy/60 p-4 backdrop-blur-sm sm:p-8" onClick={event => {
    if (event.target === event.currentTarget) setExpanded(false);
  }} data-id="element-786" data-mp="8k97f4p0g3c9">
      {card}
    </div>;
}